/**
 * Admin V3 — Facture detail page
 *
 * Mounted at: /admin-v3/billing/invoices/:invoiceId
 * Source: GET /admin/billing/invoices/:id → { invoice, ledger_entries }.
 *
 * Surfaces the activated Facture lifecycle through the UI:
 *   - Download PDF        GET  …/invoices/:id/pdf        (ADMIN_PARTNER_READ)
 *   - Issue (draft only)  POST …/invoices/:id/issue      (ADMIN_PARTNER_UPDATE)
 *   - Void (reasoned)     POST …/invoices/:id/void       (ADMIN_PARTNER_UPDATE)
 *   - Create Avoir        POST …/avoirs                  (ADMIN_PARTNER_UPDATE)
 *
 * RBAC: backend @rbac_permission is authoritative. Mutation affordances are
 * additionally hidden when usePermissions().can('admin:partner:update') is false
 * (HIDE posture). PDF download requires only read and is always offered.
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText, ArrowLeft, Download, CheckCircle2, XCircle, FilePlus2, Loader2, ChevronDown, Send,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SectionCard, SmallPill, currency, invoiceStatusMeta, formatAddressLines, cn } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import usePdfDownload from '../../shared/hooks/usePdfDownload';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const PERM_UPDATE = 'admin:partner:update';

const fmtDate = (iso, locale = 'fr') => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' })
      .format(typeof iso === 'string' ? new Date(iso) : iso);
  } catch { return String(iso); }
};

const Field = ({ label, children, accent }) => (
  <div className="text-[12px]">
    <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">{label}</dt>
    <dd className={cn('font-medium', accent || 'text-slate-700')}>{children}</dd>
  </div>
);

const BillingInvoiceDetailPage = () => {
  const locale = DEFAULT_LOCALE;
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { can, loading: permsLoading } = usePermissions();
  const { showSuccess, showError } = useToast();

  const { data, loading, error, refetch } = useAdminQuery(`/admin/billing/invoices/${invoiceId}`);
  const invoice = data?.invoice || null;

  const issueM = useAdminMutation(`/admin/billing/invoices/${invoiceId}/issue`, 'post');
  const voidM = useAdminMutation(`/admin/billing/invoices/${invoiceId}/void`, 'post');
  const createAvoirM = useAdminMutation('/admin/billing/avoirs', 'post');
  const resendM = useAdminMutation(`/admin/billing/invoices/${invoiceId}/resend`, 'post');
  const pdf = usePdfDownload();

  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [avoirOpen, setAvoirOpen] = useState(false);
  const [avoirType, setAvoirType] = useState('full');
  const [avoirReason, setAvoirReason] = useState('');
  const [avoirAmount, setAvoirAmount] = useState('');

  const canUpdate = can(PERM_UPDATE);
  const status = invoice?.status;
  const isDraft = status === 'draft';
  const isIssued = status === 'issued' || status === 'partially_paid';
  const isVoidable = isDraft || status === 'issued' || status === 'overdue' || status === 'partially_paid';

  const handlePdf = () => {
    pdf.download(`/admin/billing/invoices/${invoiceId}/pdf`, `${invoice?.invoice_number || 'facture'}.pdf`)
      .then((ok) => { if (!ok) showError(pdf.error || t('billing.invoiceDetail.pdf.error', locale)); });
  };

  const handleIssue = () => {
    issueM.mutate({}, {
      // F1: the invoice is always issued (commit-then-deliver). Inspect the
      // delivery outcome so a failed email surfaces a warning, not a green "OK".
      onSuccess: (res) => {
        if (res?.delivery && res.delivery.delivered === false) {
          const why = res.delivery.error ? `: ${res.delivery.error}` : '';
          showError(t('billing.invoiceDetail.issued.warnEmail', locale) + why);
        } else {
          showSuccess(t('billing.invoiceDetail.issued.ok', locale));
        }
        refetch();
      },
      onError: (e) => showError(e?.response?.data?.detail || t('billing.invoiceDetail.action.error', locale)),
    });
  };

  const handleResend = () => {
    resendM.mutate({}, {
      onSuccess: (res) => {
        if (res?.delivery && res.delivery.delivered === false) {
          const why = res.delivery.error ? `: ${res.delivery.error}` : '';
          showError(t('billing.delivery.retry.failed', locale) + why);
        } else {
          showSuccess(t('billing.delivery.retry.ok', locale));
        }
        refetch();
      },
      onError: (e) => showError(e?.response?.data?.detail || t('billing.delivery.retry.failed', locale)),
    });
  };

  const handleVoid = () => {
    voidM.mutate({ reason: voidReason }, {
      onSuccess: () => { showSuccess(t('billing.invoiceDetail.voided.ok', locale)); setVoidOpen(false); setVoidReason(''); refetch(); },
      onError: (e) => showError(e?.response?.data?.detail || t('billing.invoiceDetail.action.error', locale)),
    });
  };

  const handleCreateAvoir = () => {
    const body = { original_invoice_id: invoiceId, type: avoirType, reason: avoirReason };
    if (avoirType === 'partial' && avoirAmount) body.credit_amount = Number(avoirAmount);
    createAvoirM.mutate(body, {
      onSuccess: (res) => {
        showSuccess(t('billing.avoirCreate.ok', locale));
        const newId = res?.avoir?.id || res?.id;
        if (newId) navigate(`/admin/billing/avoirs/${newId}`);
        else { setAvoirOpen(false); refetch(); }
      },
      onError: (e) => showError(e?.response?.data?.detail || t('billing.invoiceDetail.action.error', locale)),
    });
  };

  const lineItems = Array.isArray(invoice?.line_items) ? invoice.line_items : [];
  const taxLabel = t('billing.invoiceDetail.field.tax', locale).replace('{rate}', String(invoice?.tax_rate ?? 0));

  return (
    <SectionCard
      title={invoice?.invoice_number || t('billing.invoiceDetail.back', locale)}
      icon={FileText}
      iconClass="bg-blue-100 text-blue-600"
      action={
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/billing/invoices')} data-testid="adminv3-invoice-detail-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" />{t('billing.invoiceDetail.back', locale)}
        </Button>
      }
    >
      {loading && !data && (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />{t('billing.invoiceDetail.loading', locale)}
        </div>
      )}

      {error && !loading && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="adminv3-invoice-detail-error">
          {error}
        </div>
      )}

      {invoice && (
        <div className="space-y-5">
          {/* Status + actions */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SmallPill meta={invoiceStatusMeta[status] || invoiceStatusMeta.draft} />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={handlePdf} disabled={pdf.loading}
                      data-testid="adminv3-invoice-pdf">
                {pdf.loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
                {t('billing.invoiceDetail.action.pdf', locale)}
              </Button>

              {/* F2: while the permissions fetch is in flight, show a stable spinner
                  placeholder so mutation buttons appear once (resolved) instead of
                  flickering in after the read-only PDF button. */}
              {permsLoading && (
                <span className="flex items-center px-2 text-slate-300" data-testid="adminv3-invoice-perms-loading">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                </span>
              )}

              {canUpdate && isDraft && (
                <Button variant="default" size="sm" onClick={handleIssue} disabled={issueM.loading}
                        data-testid="adminv3-invoice-issue">
                  {issueM.loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                  {t('billing.invoiceDetail.action.issue', locale)}
                </Button>
              )}

              {canUpdate && isVoidable && status !== 'voided' && (
                <Button variant="outline" size="sm" onClick={() => setVoidOpen((v) => !v)}
                        className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="adminv3-invoice-void-toggle">
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />{t('billing.invoiceDetail.action.void', locale)}
                </Button>
              )}

              {canUpdate && isIssued && (
                <Button variant="outline" size="sm" onClick={() => setAvoirOpen((v) => !v)}
                        data-testid="adminv3-invoice-create-avoir-toggle">
                  <FilePlus2 className="h-3.5 w-3.5 mr-1.5" />{t('billing.invoiceDetail.action.createAvoir', locale)}
                </Button>
              )}
            </div>
          </div>

          {/* Void inline form */}
          {voidOpen && canUpdate && (
            <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4 space-y-2" data-testid="adminv3-invoice-void-form">
              <label className="text-[12px] font-semibold text-rose-700">{t('billing.invoiceDetail.void.reason', locale)}</label>
              <textarea value={voidReason} onChange={(e) => setVoidReason(e.target.value)} rows={2}
                        className="w-full rounded-lg border border-rose-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-rose-100" />
              <div className="flex gap-2">
                <Button variant="default" size="sm" onClick={handleVoid}
                        disabled={voidM.loading || voidReason.trim().length < 5}
                        className="bg-rose-600 hover:bg-rose-700" data-testid="adminv3-invoice-void-confirm">
                  {voidM.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                  {t('billing.invoiceDetail.void.confirm', locale)}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setVoidOpen(false)}>{t('billing.invoiceDetail.cancel', locale)}</Button>
              </div>
            </div>
          )}

          {/* Create-avoir inline form */}
          {avoirOpen && canUpdate && (
            <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4 space-y-3" data-testid="adminv3-invoice-avoir-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[12px] font-semibold text-slate-600">{t('billing.avoirCreate.type', locale)}</label>
                  <div className="relative">
                    <select value={avoirType} onChange={(e) => setAvoirType(e.target.value)}
                            className="w-full appearance-none rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 pr-9 text-sm text-slate-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-testid="adminv3-invoice-avoir-type">
                      <option value="full">{t('billing.avoirCreate.type.full', locale)}</option>
                      <option value="partial">{t('billing.avoirCreate.type.partial', locale)}</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                </div>
                {avoirType === 'partial' && (
                  <div>
                    <label className="text-[12px] font-semibold text-slate-600">{t('billing.avoirCreate.amount', locale)}</label>
                    <input type="number" min="0" step="0.01" value={avoirAmount} onChange={(e) => setAvoirAmount(e.target.value)}
                           className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-testid="adminv3-invoice-avoir-amount" />
                  </div>
                )}
              </div>
              <div>
                <label className="text-[12px] font-semibold text-slate-600">{t('billing.avoirCreate.reason', locale)}</label>
                <textarea value={avoirReason} onChange={(e) => setAvoirReason(e.target.value)} rows={2}
                          className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-testid="adminv3-invoice-avoir-reason" />
              </div>
              <div className="flex gap-2">
                <Button variant="default" size="sm" onClick={handleCreateAvoir}
                        disabled={createAvoirM.loading || avoirReason.trim().length < 3 || (avoirType === 'partial' && !avoirAmount)}
                        data-testid="adminv3-invoice-avoir-submit">
                  {createAvoirM.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                  {t('billing.avoirCreate.submit', locale)}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setAvoirOpen(false)}>{t('billing.invoiceDetail.cancel', locale)}</Button>
              </div>
            </div>
          )}

          {/* Partner */}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">
              {t('billing.invoiceDetail.section.partner', locale)}
            </div>
            <div className="text-slate-900 font-medium">{invoice.partner_name || '—'}</div>
            <div className="text-[12px] text-slate-500">{invoice.partner_email || ''}</div>
            <div className="text-[12px] text-slate-500 whitespace-pre-line">{formatAddressLines(invoice.partner_address)}</div>
            {invoice.partner_vat_number && (
              <div className="text-[12px] text-slate-500 font-mono mt-1">{invoice.partner_vat_number}</div>
            )}
          </div>

          {/* Email delivery (F1) — visible once issued; operator sees status + can retry */}
          {!isDraft && (
            <div className="rounded-xl border border-slate-200 p-4" data-testid="adminv3-invoice-delivery">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                  {t('billing.delivery.section', locale)}
                </div>
                {canUpdate && status !== 'voided' && (
                  <Button variant="outline" size="sm" onClick={handleResend} disabled={resendM.loading}
                          data-testid="adminv3-invoice-resend">
                    {resendM.loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                    {t('billing.delivery.retry', locale)}
                  </Button>
                )}
              </div>
              {invoice.delivered_at ? (
                <div className="text-[12px] font-medium text-emerald-700" data-testid="adminv3-invoice-delivery-status">
                  {t('billing.delivery.sent', locale)} — {fmtDate(invoice.delivered_at, locale)}
                </div>
              ) : invoice.last_delivery_error ? (
                <div className="text-[12px] font-medium text-rose-700" data-testid="adminv3-invoice-delivery-status">
                  {t('billing.delivery.failed', locale)}
                </div>
              ) : (
                <div className="text-[12px] text-slate-500" data-testid="adminv3-invoice-delivery-status">
                  {t('billing.delivery.notSent', locale)}
                </div>
              )}
              {invoice.last_delivery_attempt_at && (
                <div className="text-[11px] text-slate-400 mt-1">
                  {t('billing.delivery.attemptAt', locale)}: {fmtDate(invoice.last_delivery_attempt_at, locale)}
                </div>
              )}
              {invoice.last_delivery_error && (
                <div className="text-[11px] text-rose-600 mt-1 break-all" data-testid="adminv3-invoice-delivery-error">
                  {t('billing.delivery.error', locale)}: {invoice.last_delivery_error}
                </div>
              )}
            </div>
          )}

          {/* Amounts */}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-3">
              {t('billing.invoiceDetail.section.amounts', locale)}
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
              <Field label={t('billing.invoiceDetail.field.subtotal', locale)}>{currency(invoice.amount ?? 0)}</Field>
              <Field label={taxLabel}>{currency(invoice.tax_amount ?? 0)}</Field>
              <Field label={t('billing.invoiceDetail.field.total', locale)} accent="text-slate-900 font-bold">{currency(invoice.total_amount ?? 0)}</Field>
              <Field label={t('billing.invoiceDetail.field.due', locale)} accent={(invoice.amount_due ?? 0) > 0 ? 'text-rose-700' : 'text-slate-400'}>{currency(invoice.amount_due ?? 0)}</Field>
              <Field label={t('billing.invoiceDetail.field.issued', locale)}>{fmtDate(invoice.issued_at || invoice.created_at, locale)}</Field>
              <Field label={t('billing.invoiceDetail.field.dueDate', locale)}>{fmtDate(invoice.due_date, locale)}</Field>
              <Field label={t('billing.invoiceDetail.field.period', locale)}>{fmtDate(invoice.period_start, locale)} → {fmtDate(invoice.period_end, locale)}</Field>
              <Field label={t('billing.invoiceDetail.field.paid', locale)} accent="text-emerald-700">{currency(invoice.amount_paid ?? 0)}</Field>
            </dl>
          </div>

          {/* Payment details */}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-3">
              {t('billing.invoiceDetail.section.payment', locale)}
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
              <Field label={t('billing.invoiceDetail.field.paymentStatus', locale)}>
                <SmallPill meta={invoiceStatusMeta[status] || invoiceStatusMeta.draft} />
              </Field>
              {invoice.payment_reference && (
                <Field label={t('billing.invoiceDetail.field.paymentRef', locale)}><span className="font-mono">{invoice.payment_reference}</span></Field>
              )}
              {invoice.paid_at && (
                <Field label={t('billing.invoiceDetail.field.paidAt', locale)}>{fmtDate(invoice.paid_at, locale)}</Field>
              )}
            </dl>
          </div>

          {/* Line items */}
          {lineItems.length > 0 && (
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">
                {t('billing.invoiceDetail.section.lineItems', locale)} ({lineItems.length})
              </div>
              <pre className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-[11px] font-mono text-slate-600 overflow-x-auto max-h-56">
{JSON.stringify(lineItems, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
};

export default BillingInvoiceDetailPage;
